import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, AlertCircle, FileDown, Lock } from "lucide-react";
import { UpgradeContent } from "@/components/UpgradeDialog";
import { useSubscription } from "@/context/SubscriptionContext";
import { PageHeader, StatCard, Disclaimer } from "@/components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, downloadFile, errMsg, fmtDate, fmtMiles, fmtMoney, EXPENSE_GROUPS } from "@/lib/api";

const thisYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => thisYear + 1 - i);

const ExportButton = ({ label, path, filename, testId, primary }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await downloadFile(path, filename);
      toast.success(`${label} downloaded`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={go} disabled={busy} className={primary ? "mt-btn-primary !h-12" : "mt-btn-outline"} data-testid={testId}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : primary ? <FileDown className="w-5 h-5" /> : <Download className="w-4 h-4" />}
      {label}
    </button>
  );
};

const Panel = ({ title, sub, amount, amountTestId, tone = "slate", children, testId }) => (
  <section className="mt-card p-5 sm:p-6 min-w-0" data-testid={testId}>
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
        {sub && <p className="text-sm text-slate-500 mt-0.5">{sub}</p>}
      </div>
      <p className={`font-mono text-xl sm:text-2xl font-bold shrink-0 ${tone === "green" ? "text-emerald-700" : "text-slate-900"}`} data-testid={amountTestId}>{fmtMoney(amount)}</p>
    </div>
    {children}
  </section>
);

const Row = ({ label, value, testId, muted }) => (
  <div className="flex justify-between gap-4 py-2.5 border-b border-dashed border-slate-200 last:border-0 text-sm" data-testid={testId}>
    <span className={muted ? "text-slate-400" : "text-slate-600"}>{label}</span>
    <span className={`font-mono font-bold ${muted ? "text-slate-400" : "text-slate-900"}`}>{value}</span>
  </div>
);

const MileagePanel = ({ r }) => (
  <Panel title="Estimated standard mileage deduction" sub={`${fmtMiles(r.total_business_miles)} business miles · ${r.business_trip_count} trips`} amount={r.mileage_deduction} amountTestId="report-mileage-deduction" tone="green" testId="report-mileage-section">
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-xs sm:text-sm" data-testid="report-rate-periods">
        <thead>
          <tr className="mt-overline text-left">
            <th className="px-1 py-2">Period</th>
            <th className="px-1 py-2 text-right">Rate</th>
            <th className="px-1 py-2 text-right">Miles</th>
            <th className="px-1 py-2 text-right">Deduction</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {r.rate_periods.map((p, i) => (
            <tr key={p.start || "unrated"} data-testid={`report-rate-period-${i}`}>
              <td className="px-1 py-2.5 text-slate-700">{p.start ? `${fmtDate(p.start)} – ${fmtDate(p.end)}` : <span className="text-red-700 font-semibold">No rate set</span>}</td>
              <td className="px-1 py-2.5 text-right font-mono">${p.rate}</td>
              <td className="px-1 py-2.5 text-right font-mono" data-testid={`report-rate-period-${i}-miles`}>{fmtMiles(p.miles)}</td>
              <td className="px-1 py-2.5 text-right font-mono font-bold text-slate-900" data-testid={`report-rate-period-${i}-deduction`}>{fmtMoney(p.deduction)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {r.unrated_business_miles > 0 && (
      <p className="mt-3 flex items-start gap-2 text-sm text-red-800 bg-red-50 rounded-lg p-3" data-testid="report-unrated-warning">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        {fmtMiles(r.unrated_business_miles)} business miles fall on dates with no mileage rate. Add a rate period in Settings.
      </p>
    )}
    <p className="text-xs text-slate-500 mt-3">Each trip uses the rate in effect on its date. Personal miles ({fmtMiles(r.total_personal_miles)}) are not included.</p>
  </Panel>
);

const catRows = (r, key) => r.expenses_by_category.filter((c) => c.group === key);

const AdditionalPanel = ({ r }) => (
  <Panel title="Additional potentially deductible expenses" sub="Can be claimed in addition to standard mileage" amount={r.additional_deductible_total} amountTestId="report-additional-total" tone="green" testId="report-additional-section">
    <p className="mt-overline mt-2 mb-1">Business parking & tolls · {fmtMoney(r.parking_tolls_total)}</p>
    {catRows(r, "parking_tolls").map((c) => <Row key={c.category} label={`${c.category} (${c.count})`} value={fmtMoney(c.total)} testId={`report-category-${c.category.toLowerCase()}`} muted={!c.count} />)}
    <p className="mt-overline mt-4 mb-1">Other business expenses · {fmtMoney(r.other_business_total)}</p>
    {catRows(r, "other_business").map((c) => <Row key={c.category} label={`${c.category} (${c.count})`} value={fmtMoney(c.total)} testId={`report-category-${c.category.toLowerCase()}`} muted={!c.count} />)}
  </Panel>
);

const VehiclePanel = ({ r }) => (
  <Panel title="Vehicle operating expenses tracked for records" sub="Not added to your total under the standard mileage method" amount={r.vehicle_operating_total} amountTestId="report-vehicle-total" testId="report-vehicle-section">
    {catRows(r, "vehicle").map((c) => <Row key={c.category} label={`${c.category} (${c.count})`} value={fmtMoney(c.total)} testId={`report-category-${c.category.toLowerCase()}`} muted={!c.count} />)}
    <p className="text-xs text-slate-500 mt-3 leading-relaxed">
      The standard mileage rate already covers the cost of running your vehicle (fuel, oil, repairs, tires, insurance, registration and similar),
      so these are kept for your records only. If your tax professional uses the actual expense method instead, these records support that calculation.
    </p>
  </Panel>
);

const TotalCard = ({ r }) => (
  <section className="rounded-2xl bg-emerald-950 text-white p-6 sm:p-8 mt-fade-up" data-testid="stat-total-estimated-deductions">
    <p className="mt-overline !text-emerald-300">Total estimated deductions · standard mileage method · {r.year}</p>
    <p className="font-mono text-4xl sm:text-5xl font-bold tracking-tight mt-3" data-testid="stat-total-estimated-deductions-value">{fmtMoney(r.total_estimated_deductions)}</p>
    <p className="text-sm text-emerald-100/80 mt-3" data-testid="report-total-formula">
      {fmtMoney(r.mileage_deduction)} standard mileage + {fmtMoney(r.additional_deductible_total)} additional expenses. Vehicle operating expenses ({fmtMoney(r.vehicle_operating_total)}) are not added.
    </p>
  </section>
);

const LockedReport = () => (
  <div className="space-y-6" data-testid="tax-report-locked">
    <PageHeader overline="Estimates · Recordkeeping" title="Tax Report" />
    <div className="mt-card p-6 sm:p-8 max-w-xl">
      <span className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
        <Lock className="w-6 h-6 text-emerald-700" />
      </span>
      <h2 className="font-heading text-2xl font-bold text-slate-900">Unlock your Tax Report</h2>
      <p className="text-slate-600 mt-2 mb-6">The Tax Report, Tax Summary PDF and CSV exports are included with MileageTax Monthly.</p>
      <UpgradeContent testId="tax-report-upgrade" />
    </div>
  </div>
);

export default function TaxReport() {
  const { sub } = useSubscription();
  if (!sub) return <div className="h-48 mt-card animate-pulse" data-testid="tax-report-loading" />;
  return sub.subscribed ? <TaxReportContent /> : <LockedReport />;
}

function TaxReportContent() {
  const [year, setYear] = useState(null);
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get("/report", { params: year ? { year } : {} }).then(({ data }) => {
      setR(data);
      if (!year) setYear(data.year);
    });
  }, [year]);

  return (
    <div className="space-y-6" data-testid="tax-report-page">
      <PageHeader overline="Estimates · Recordkeeping" title="Tax Report">
        {r && (
          <ExportButton primary label="Download Tax Summary PDF" path={`/export/tax-summary?year=${r.year}`} filename={`mileagetax-tax-summary-${r.year}.pdf`} testId="tax-report-download-pdf" />
        )}
        <Select value={year ? String(year) : undefined} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-12 w-full sm:w-40 text-base font-semibold bg-white" data-testid="tax-report-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
          <SelectContent className="bg-white">
            {YEARS.map((y) => <SelectItem key={y} value={String(y)} data-testid={`tax-report-year-${y}`}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>
      <Disclaimer />
      {!r ? (
        <div className="h-48 mt-card animate-pulse" />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            <StatCard label="Total business miles" value={fmtMiles(r.total_business_miles)} sub={`${r.business_trip_count} business trips`} testId="stat-business-miles" />
            <StatCard label="Est. standard mileage deduction" value={fmtMoney(r.mileage_deduction)} sub="rate by trip date" testId="stat-mileage-deduction" accent="green" delay={60} />
            <StatCard label="Additional deductible expenses" value={fmtMoney(r.additional_deductible_total)} sub="parking, tolls & other business" testId="stat-additional-deductible" accent="green" delay={120} />
            <StatCard label="Vehicle expenses (records)" value={fmtMoney(r.vehicle_operating_total)} sub="not added to total" testId="stat-vehicle-operating" delay={180} />
          </div>
          <TotalCard r={r} />
          <div className="grid lg:grid-cols-2 gap-5">
            <MileagePanel r={r} />
            <AdditionalPanel r={r} />
          </div>
          <VehiclePanel r={r} />
          <p className="text-sm text-slate-500 px-1" data-testid="report-total-recorded">
            Total of all recorded expenses in {r.year}: <strong className="font-mono text-slate-800">{fmtMoney(r.total_recorded_expenses)}</strong> ({EXPENSE_GROUPS.map((g) => g.short).join(", ")}).
          </p>
          <div className="mt-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="report-export-section">
            <div>
              <h3 className="font-heading text-lg font-bold text-slate-900">Export your records</h3>
              <p className="text-sm text-slate-500">Download {r.year} records as spreadsheet (CSV) files for your tax preparer.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <ExportButton label="Trips CSV" path={`/export/trips?year=${r.year}`} filename={`mileagetax-trips-${r.year}.csv`} testId="tax-report-export-csv" />
              <ExportButton label="Expenses CSV" path={`/export/expenses?year=${r.year}`} filename={`mileagetax-expenses-${r.year}.csv`} testId="tax-report-export-expenses-csv" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
